import axios from 'axios';
import { ApiError, api } from './client';
import type {
  Address,
  Cart,
  Category,
  CategorySavePayload,
  Comment,
  CommentSavePayload,
  DashboardSummary,
  OrderCheckout,
  OrderDetail,
  OrderSummary,
  PageResponse,
  Payment,
  PostDetail,
  PostSavePayload,
  PostSummary,
  PresignResult,
  ProductDetail,
  ProductSavePayload,
  ProductSearchParams,
  ProductSummary,
  Review,
  ReviewComment,
  ReviewSavePayload,
  ReviewStats,
  SalesStat,
  TokenResponse,
  TopProduct,
  UploadPolicy,
  UploadResult,
  User,
} from './types';

// ---------------- 인증 ----------------

export const authApi = {
  signup: (body: {
    email: string;
    password: string;
    name: string;
    nickname?: string;
    phone?: string;
    marketingAgreed?: boolean;
  }) => api.post<User>('/api/auth/signup', body),

  login: (body: { email: string; password: string }) =>
    api.post<TokenResponse>('/api/auth/login', body),

  refresh: (refreshToken: string) => api.post<TokenResponse>('/api/auth/refresh', { refreshToken }),

  logout: (refreshToken: string) =>
    api.post<void>(`/api/auth/logout?refreshToken=${encodeURIComponent(refreshToken)}`),

  /** 소셜 로그인은 페이지 이동이므로 URL 만 만들어 준다. */
  socialLoginUrl: (provider: 'google' | 'naver' | 'kakao') => `/oauth2/authorization/${provider}`,
};

// ---------------- 내 정보 ----------------

export const userApi = {
  me: () => api.get<User>('/api/users/me'),
  update: (body: Partial<Pick<User, 'name' | 'nickname' | 'phone' | 'profileImageUrl'>> & {
    marketingAgreed?: boolean;
  }) => api.patch<User>('/api/users/me', body),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.patch<void>('/api/users/me/password', body),
  withdraw: () => api.delete<void>('/api/users/me'),

  addresses: () => api.get<Address[]>('/api/users/me/addresses'),
  addAddress: (body: Omit<Address, 'addressId'>) =>
    api.post<Address>('/api/users/me/addresses', body),
  updateAddress: (addressId: number, body: Omit<Address, 'addressId'>) =>
    api.put<Address>(`/api/users/me/addresses/${addressId}`, body),
  deleteAddress: (addressId: number) => api.delete<void>(`/api/users/me/addresses/${addressId}`),
};

// ---------------- 상품 / 카테고리 ----------------

export const productApi = {
  list: (params: ProductSearchParams = {}) =>
    api.get<PageResponse<ProductSummary>>('/api/products', { params }),
  detail: (productId: number) => api.get<ProductDetail>(`/api/products/${productId}`),
  detailBySlug: (slug: string) => api.get<ProductDetail>(`/api/products/slug/${slug}`),
};

export const categoryApi = {
  tree: () => api.get<Category[]>('/api/categories'),
};

// ---------------- 장바구니 ----------------

export const cartApi = {
  get: () => api.get<Cart>('/api/cart'),
  add: (body: { productId: number; optionId: number; quantity: number }) =>
    api.post<Cart>('/api/cart/items', body),
  changeQuantity: (cartItemId: number, quantity: number) =>
    api.patch<Cart>(`/api/cart/items/${cartItemId}`, { quantity }),
  remove: (cartItemId: number) => api.delete<Cart>(`/api/cart/items/${cartItemId}`),
  clear: () => api.delete<void>('/api/cart'),
  count: () => api.get<number>('/api/cart/count'),
};

// ---------------- 주문 / 결제 ----------------

export const orderApi = {
  create: (body: {
    cartItemIds?: number[];
    lines?: { productId: number; optionId: number; quantity: number }[];
    recipient: string;
    recipientPhone: string;
    zipcode: string;
    address1: string;
    address2?: string;
    deliveryMemo?: string;
  }) => api.post<OrderCheckout>('/api/orders', body),

  list: (params: { page?: number; size?: number; status?: string } = {}) =>
    api.get<PageResponse<OrderSummary>>('/api/orders', { params }),

  detail: (orderId: number) => api.get<OrderDetail>(`/api/orders/${orderId}`),
  confirm: (orderId: number) => api.patch<void>(`/api/orders/${orderId}/confirm`),
  cancel: (orderId: number, reason: string) =>
    api.post<unknown>(`/api/orders/${orderId}/cancel`, { reason }),
  refund: (orderId: number, reason: string) =>
    api.post<unknown>(`/api/orders/${orderId}/refund`, { reason }),
};

export const paymentApi = {
  confirm: (body: { paymentKey: string; orderNo: string; amount: number; cartItemIds?: number[] }) =>
    api.post<Payment>('/api/payments/confirm', body),
  byOrder: (orderId: number) => api.get<Payment>(`/api/payments/orders/${orderId}`),
  /** 개발 환경 전용. 토스 결제 없이 0원 결제 완료로 처리한다. */
  devApprove: (body: { orderNo: string; cartItemIds?: number[] }) =>
    api.post<Payment>('/api/payments/dev-approve', body),
};

// ---------------- 리뷰 ----------------

export const reviewApi = {
  list: (params: {
    productId?: number;
    page?: number;
    size?: number;
    rating?: number;
    withMediaOnly?: boolean;
    sortBy?: string;
  }) => api.get<PageResponse<Review>>('/api/reviews', { params }),

  stats: (productId: number) => api.get<ReviewStats>('/api/reviews/stats', { params: { productId } }),
  mine: (params: { page?: number; size?: number } = {}) =>
    api.get<PageResponse<Review>>('/api/reviews/me', { params }),
  create: (body: ReviewSavePayload & { orderItemId: number }) => api.post<Review>('/api/reviews', body),
  // 수정도 media 를 전량 교체한다. 빼고 보내면 첨부가 모두 사라진다.
  update: (reviewId: number, body: ReviewSavePayload) => api.put<Review>(`/api/reviews/${reviewId}`, body),
  remove: (reviewId: number) => api.delete<void>(`/api/reviews/${reviewId}`),
  helpful: (reviewId: number) => api.patch<void>(`/api/reviews/${reviewId}/helpful`),

  // 리뷰 댓글 — 목록은 commentCount 를 보고 필요할 때만 부른다.
  comments: (reviewId: number) => api.get<ReviewComment[]>(`/api/reviews/${reviewId}/comments`),
  addComment: (reviewId: number, body: CommentSavePayload) =>
    api.post<ReviewComment>(`/api/reviews/${reviewId}/comments`, body),
  updateComment: (commentId: number, body: CommentSavePayload) =>
    api.put<ReviewComment>(`/api/reviews/comments/${commentId}`, body),
  removeComment: (commentId: number) => api.delete<void>(`/api/reviews/comments/${commentId}`),
};

// ---------------- 게시판 ----------------

export const boardApi = {
  list: (params: {
    boardType?: string;
    keyword?: string;
    productId?: number;
    status?: string;
    page?: number;
    size?: number;
  }) => api.get<PageResponse<PostSummary>>('/api/boards/posts', { params }),

  mine: (params: { page?: number; size?: number } = {}) =>
    api.get<PageResponse<PostSummary>>('/api/boards/posts/me', { params }),

  detail: (postId: number) => api.get<PostDetail>(`/api/boards/posts/${postId}`),

  create: (body: PostSavePayload) => api.post<PostDetail>('/api/boards/posts', body),

  // 수정은 첨부를 전량 교체한다. 남길 이미지는 그대로 다시 실어 보내야 한다.
  update: (postId: number, body: PostSavePayload) => api.put<PostDetail>(`/api/boards/posts/${postId}`, body),

  remove: (postId: number) => api.delete<void>(`/api/boards/posts/${postId}`),

  addComment: (postId: number, body: CommentSavePayload) =>
    api.post<Comment>(`/api/boards/posts/${postId}/comments`, body),
  updateComment: (commentId: number, body: CommentSavePayload) =>
    api.put<Comment>(`/api/boards/comments/${commentId}`, body),
  removeComment: (commentId: number) => api.delete<void>(`/api/boards/comments/${commentId}`),
};

// ---------------- 파일 ----------------

/**
 * presigned URL 로 S3 에 직접 PUT 한다.
 * 우리 API 가 아니므로 Authorization 헤더를 붙이는 공용 인스턴스를 쓰지 않는다.
 * Content-Type 은 서명에 들어 있어 발급받은 값과 정확히 같아야 한다.
 */
async function putToStorage(uploadUrl: string, body: Blob, contentType: string, onProgress?: (loaded: number) => void) {
  try {
    await axios.put(uploadUrl, body, {
      headers: { 'Content-Type': contentType },
      timeout: 0,
      onUploadProgress: (event) => onProgress?.(event.loaded),
    });
  } catch (e) {
    const status = axios.isAxiosError(e) ? e.response?.status : undefined;
    throw new ApiError('파일 업로드에 실패했습니다. 잠시 후 다시 시도해 주세요.', 'FILE_UPLOAD_FAILED', status);
  }
}

export const fileApi = {
  /** 업로드 URL 발급. 서버가 형식·크기·권한을 검사한다. */
  presign: (body: { prefix: string; filename: string; size: number; thumbnailSize?: number }) =>
    api.post<PresignResult>('/api/files/presign', body),

  /**
   * 원본과 (선택) 썸네일을 브라우저에서 S3 로 바로 올린다. 파일 본문은 우리 서버를 거치지 않는다.
   * onProgress 는 원본 기준 누적 바이트.
   */
  upload: async (
    file: File,
    prefix: string,
    options: { thumbnail?: Blob | null; onProgress?: (loaded: number) => void } = {},
  ): Promise<UploadResult> => {
    const { thumbnail, onProgress } = options;
    const ticket = await fileApi.presign({
      prefix,
      filename: file.name,
      size: file.size,
      thumbnailSize: thumbnail?.size,
    });
    await putToStorage(ticket.uploadUrl, file, ticket.contentType, onProgress);
    if (thumbnail && ticket.thumbnailUploadUrl) {
      await putToStorage(ticket.thumbnailUploadUrl, thumbnail, 'image/jpeg');
    }
    return {
      objectKey: ticket.objectKey,
      url: ticket.url,
      mediaType: ticket.mediaType,
      thumbnailObjectKey: ticket.thumbnailObjectKey,
      thumbnailUrl: ticket.thumbnailUrl,
    };
  },

  policy: () => api.get<UploadPolicy>('/api/files/policy'),
};

// ---------------- 관리자 ----------------

export const adminApi = {
  // 대시보드
  summary: () => api.get<DashboardSummary>('/api/admin/dashboard/summary'),
  sales: (params: { period?: string; fromDate?: string; toDate?: string } = {}) =>
    api.get<SalesStat>('/api/admin/dashboard/sales', { params }),
  signups: (params: { period?: string; fromDate?: string; toDate?: string } = {}) =>
    api.get<SalesStat>('/api/admin/dashboard/signups', { params }),
  topProducts: (params: { limit?: number } = {}) =>
    api.get<TopProduct[]>('/api/admin/dashboard/top-products', { params }),

  // 상품
  products: (params: ProductSearchParams = {}) =>
    api.get<PageResponse<ProductSummary>>('/api/admin/products', { params }),
  productDetail: (productId: number) => api.get<ProductDetail>(`/api/admin/products/${productId}`),
  createProduct: (body: ProductSavePayload) => api.post<ProductDetail>('/api/admin/products', body),
  updateProduct: (productId: number, body: ProductSavePayload) =>
    api.put<ProductDetail>(`/api/admin/products/${productId}`, body),
  changeProductStatus: (productId: number, status: string) =>
    api.patch<void>(`/api/admin/products/${productId}/status?status=${status}`),
  /** 메인 추천 노출 토글. 베스트(판매량 순)는 자동 집계라 조작 대상이 아니다. */
  setProductFeatured: (productId: number, featured: boolean) =>
    api.patch<void>(`/api/admin/products/${productId}/featured?featured=${featured}`),
  deleteProduct: (productId: number) => api.delete<void>(`/api/admin/products/${productId}`),

  // 카테고리 (비활성 포함 전체 트리)
  categories: () => api.get<Category[]>('/api/admin/categories'),
  createCategory: (body: CategorySavePayload) => api.post<Category>('/api/admin/categories', body),
  updateCategory: (categoryId: number, body: CategorySavePayload) =>
    api.put<Category>(`/api/admin/categories/${categoryId}`, body),
  deleteCategory: (categoryId: number) => api.delete<void>(`/api/admin/categories/${categoryId}`),

  // 재고
  adjustStock: (body: { optionId: number; changeQty: number; changeType: string; memo?: string }) =>
    api.post<unknown>('/api/admin/inventory/adjust', body),
  lowStock: (limit = 20) => api.get<unknown[]>(`/api/admin/inventory/low-stock?limit=${limit}`),

  // 주문
  orders: (params: { page?: number; size?: number; status?: string; keyword?: string } = {}) =>
    api.get<PageResponse<OrderSummary>>('/api/admin/orders', { params }),
  orderDetail: (orderId: number) => api.get<OrderDetail>(`/api/admin/orders/${orderId}`),
  prepare: (orderId: number) => api.patch<void>(`/api/admin/orders/${orderId}/prepare`),
  ship: (orderId: number, body: { courier: string; trackingNo: string }) =>
    api.patch<void>(`/api/admin/orders/${orderId}/ship`, body),
  deliver: (orderId: number) => api.patch<void>(`/api/admin/orders/${orderId}/deliver`),
  /** 배송 후 접수된 취소/반품 요청을 승인해 취소를 확정한다. */
  approveCancel: (orderId: number) => api.patch<unknown>(`/api/admin/orders/${orderId}/approve-cancel`),
  /** 취소 확정된 주문의 환불 완료 처리. 출고 이력에 따라 배송전/배송후 환불 완료로 갈린다. */
  completeRefund: (orderId: number) => api.patch<void>(`/api/admin/orders/${orderId}/refund-complete`),
  cancelOrder: (orderId: number, reason: string) =>
    api.post<void>(`/api/admin/orders/${orderId}/cancel`, { reason }),
  pendingCancels: (params: { page?: number; size?: number } = {}) =>
    api.get<PageResponse<unknown>>('/api/admin/orders/cancels/pending', { params }),
  decideCancel: (cancelId: number, approve: boolean, rejectReason?: string) =>
    api.patch<unknown>(`/api/admin/orders/cancels/${cancelId}`, { approve, rejectReason }),

  // 회원 (admin 전용)
  users: (params: { page?: number; size?: number; keyword?: string; role?: string } = {}) =>
    api.get<PageResponse<User>>('/api/admin/users', { params }),
  changeUserRole: (userId: number, role: string) =>
    api.patch<void>(`/api/admin/users/${userId}/role?role=${role}`),
  changeUserStatus: (userId: number, status: string) =>
    api.patch<void>(`/api/admin/users/${userId}/status?status=${status}`),
};
